import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "closed_won", "closed_lost", "spam"];

export default function AdminLeads() {
  const [filters, setFilters] = useState({ q: "", status: "", source: "" });
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [page, setPage] = useState(1);
  const load = async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      setData((await api.get(`/admin/leads?${params.toString()}`)).data);
    } catch {}
  };
  useEffect(() => { load(); }, [filters, page]);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/admin/leads/${lead_id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <header><h1 className="font-serif text-3xl text-ink tracking-tight">Leads</h1><p className="text-ink-soft text-sm mt-1">{data.total} total</p></header>
      <div className="flex flex-wrap gap-3 items-center">
        <input value={filters.q} onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }} placeholder="Search…"
          className="bg-bone border border-stone/40 px-3 py-2 text-sm w-72" data-testid="leads-search" />
        <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          className="bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="leads-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => { setFilters({ ...filters, source: e.target.value }); setPage(1); }}
          className="bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="leads-source-filter">
          <option value="">All sources</option>
          <option value="contact_form">contact_form</option>
          <option value="tool_run">tool_run</option>
          <option value="newsletter">newsletter</option>
        </select>
      </div>

      <div className="border border-stone/40 bg-bone" data-testid="leads-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-bone-dim border-b border-stone/40 text-[10px] uppercase tracking-[0.18em] text-stone">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Created</div>
          <div className="col-span-1"></div>
        </div>
        {data.items.length === 0 ? <div className="px-4 py-6 text-sm text-ink-soft">No leads match.</div> : data.items.map((l) => (
          <div key={l.lead_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-stone/20 items-baseline" data-testid={`lead-row-${l.lead_id}`}>
            <div className="col-span-3 text-sm text-ink truncate">{l.name || "—"}</div>
            <div className="col-span-3 text-xs text-ink-soft truncate font-mono">{l.email}</div>
            <div className="col-span-2 text-[11px] text-ink-soft truncate">{l.source}</div>
            <div className="col-span-2">
              <select value={l.status || "new"} onChange={(e) => updateStatus(l.lead_id, e.target.value)}
                className="w-full bg-bone border border-stone/40 px-2 py-1 text-[11px]" data-testid={`lead-status-${l.lead_id}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-1 text-[10px] text-stone font-mono tabular-nums">{(l.created_at || "").slice(0, 10)}</div>
            <div className="col-span-1 text-right">
              <Link to={`/admin/leads/${l.lead_id}`} className="text-[10px] uppercase tracking-[0.22em] text-ivy hover:text-brass">View →</Link>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center text-xs text-stone">
        <span>Page {data.page} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
        <div className="flex gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Prev</button>
          <button disabled={page * data.page_size >= data.total} onClick={() => setPage(page + 1)} className="border border-stone/40 px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}
